import { Implementer } from '@volkswagen-onehub/oneaudi-os-onesight';
import { TrackingAdapter } from './tracking-adapter';
/**
 * Method to create and fully bootstrap a oneAudiOS compatible TrackingService.
 * After calling this, you can directly start with tracking.
 *
 * @param options Configuration
 *
 *
 * Example:
 * ```
 * const trackingService = createAndBootstrapTrackingService({
 *     appId: 'oneaudi-myapp-id',
 *     appName: __FEATURE_APP_NAME__,
 *     appVersion: __FEATURE_APP_VERSION__
 * });
 * trackingService.track({ ... });
 * ```
 */
export function createAndBootstrapTrackingService(options) {
    const adapter = createTrackingService(options.appId, options.init?.datalayer, options.init?.urlSanitizer, options.init?.logger);
    adapter.featureAppName = options.appName;
    adapter.updateComponent({
        componentInfo: {
            // componentID is needed, but defined as "never"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            componentID: options.appId,
        },
        attributes: {
            implementer: options.implementer ?? Implementer.UNKNOWN,
            version: options.appVersion,
            absoluteNumber: options.absoluteNumber ?? 1,
            instanceNumber: options.instanceNumber ?? 1,
        },
    });
    return adapter;
}
/**
 * Method to create a oneAudiOS compatible TrackingService.
 *
 * Make sure to set `featureAppName` afterwards.
 * Certain methods will throw an error if the feature app's name is not defined. Setting
 * `trackingService.featureAppName` ensures the existence of the component.
 *
 * @param appId Mandatory: id of the feature app which wants to use tracking
 * @param datalayer Optional: The datalayer instance. If absent, a new instance will be created.
 * Existing `window.digitalData` will be reused
 * @param urlSanitizer Optional: The sanitizer to be used for URLs. If absent, a default will be
 *  used which strips away personal information.
 * @param logger Optional: The Logger. If absent, the console will be used for logging
 *
 * Example:
 * ```
 * const trackingService = createTrackingService('app-id');
 * trackingService.featureAppName = 'my-feature-app';
 * trackingService.track({ ... });
 * ```
 */
export function createTrackingService(appId, datalayer, urlSanitizer, logger) {
    return new TrackingAdapter(appId, datalayer, urlSanitizer, logger);
}
